import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useMessageTemplates, AVAILABLE_VARIABLES, type MessageTemplate } from '@/hooks/useMessageTemplates';
import { useAuth } from '@/contexts/AuthContext';
import { Plus, Pencil, Trash2, Mail, Star, Loader2, Eye, Code, Video, Users, Briefcase, UserCheck, X, Send, Info } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

/* ────────────────────── Template type config ────────────────────── */

const TEMPLATE_TYPES = [
  { value: 'interview_invite', labelKey: 'hire.emailTemplates.types.interviewInvite', icon: Video, color: 'text-primary' },
  { value: 'f2f_interview', labelKey: 'hire.emailTemplates.types.f2fInterview', icon: Users, color: 'text-blue-600' },
  { value: 'job_invitation', labelKey: 'hire.emailTemplates.types.jobInvitation', icon: Briefcase, color: 'text-amber-600' },
  { value: 'offer', labelKey: 'hire.emailTemplates.types.offer', icon: Send, color: 'text-success' },
  { value: 'reject', labelKey: 'hire.emailTemplates.types.reject', icon: X, color: 'text-destructive' },
  { value: 'hired', labelKey: 'hire.emailTemplates.types.hired', icon: UserCheck, color: 'text-emerald-600' },
  { value: 'general', labelKey: 'hire.emailTemplates.types.general', icon: Mail, color: 'text-muted-foreground' },
];

const DEFAULT_TEMPLATE_TYPES = ['interview_invite', 'f2f_interview', 'job_invitation', 'offer', 'reject', 'hired'] as const;

const restoreTemplateVariables = (value: string) => value.replace(/__([a-z_]+)__/g, '{{$1}}');

function buildDefaultTemplates(t: TFunction): Record<string, { subject: string; body_html: string }> {
  return Object.fromEntries(DEFAULT_TEMPLATE_TYPES.map((type) => [
    type,
    {
      subject: restoreTemplateVariables(t(`hire.emailTemplates.defaults.${type}.subject`)),
      body_html: restoreTemplateVariables(t(`hire.emailTemplates.defaults.${type}.bodyHtml`)),
    },
  ]));
}

/* ────────────────────── Variables per template type ────────────────────── */

const VARIABLES_BY_TYPE: Record<string, string[]> = {
  interview_invite: ['{{candidate_name}}', '{{job_title}}', '{{company_name}}', '{{interview_link}}'],
  f2f_interview: ['{{candidate_name}}', '{{job_title}}', '{{company_name}}', '{{f2f_date}}', '{{f2f_location}}', '{{f2f_notes}}'],
  job_invitation: ['{{candidate_name}}', '{{job_title}}', '{{company_name}}', '{{portal_link}}'],
  offer: ['{{candidate_name}}', '{{job_title}}', '{{company_name}}', '{{offer_link}}'],
  reject: ['{{candidate_name}}', '{{job_title}}', '{{company_name}}'],
  hired: ['{{candidate_name}}', '{{job_title}}', '{{company_name}}'],
  general: ['{{candidate_name}}', '{{candidate_email}}', '{{job_title}}', '{{company_name}}', '{{portal_link}}'],
};

/* ────────────────────── Main component ────────────────────── */

export default function EmailTemplatesManager() {
  const { t } = useTranslation();
  const defaultTemplates = useMemo(() => buildDefaultTemplates(t), [t]);
  const { templates, isLoading, upsertTemplate, deleteTemplate } = useMessageTemplates();
  const { profile, role } = useAuth();
  const { toast } = useToast();
  const isAdmin = role === 'org_owner' || role === 'org_admin' || role === 'recruiter';

  const [editDialog, setEditDialog] = useState(false);
  const [editing, setEditing] = useState<Partial<MessageTemplate> | null>(null);
  const [editorTab, setEditorTab] = useState<string>('visual');
  const [deleteTemplateTarget, setDeleteTemplateTarget] = useState<{ id: string; name: string } | null>(null);

  const openNew = (type?: string) => {
    const defaults = type ? defaultTemplates[type] : undefined;
    setEditing({
      template_type: type || 'general',
      name: type ? t(TEMPLATE_TYPES.find(item => item.value === type)?.labelKey || 'hire.emailTemplates.types.general') : '',
      subject: defaults?.subject || '',
      body_html: defaults?.body_html || '',
      is_default: false,
      is_active: true,
    });
    setEditorTab('visual');
    setEditDialog(true);
  };

  const openEdit = (tpl: MessageTemplate) => {
    setEditing({ ...tpl });
    setEditorTab('visual');
    setEditDialog(true);
  };

  const handleSave = () => {
    if (!editing || !profile?.org_id) return;
    if (!editing.name?.trim() || !editing.subject?.trim()) {
      toast({ title: t('hire.emailTemplates.validation.nameAndSubjectRequired'), variant: 'destructive' });
      return;
    }
    upsertTemplate.mutate(
      { ...editing, org_id: profile.org_id },
      { onSuccess: () => setEditDialog(false) }
    );
  };

  const insertVariable = (token: string) => {
    if (!editing) return;
    setEditing(prev => ({
      ...prev,
      body_html: (prev?.body_html || '') + token,
    }));
  };

  const currentTypeVars = VARIABLES_BY_TYPE[editing?.template_type || 'general'] || VARIABLES_BY_TYPE.general;
  const filteredVars = AVAILABLE_VARIABLES.filter(v => currentTypeVars.includes(v.token));

  const groupedTemplates = TEMPLATE_TYPES.map(type => ({
    ...type,
    templates: templates.filter(t => t.template_type === type.value),
  }));

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display font-semibold text-foreground text-lg flex items-center gap-2">
            <Mail className="w-5 h-5 text-accent" />
            {t('hire.emailTemplates.managerTitle')}
          </h2>
          <p className="text-xs text-muted-foreground mt-1">
            {t('hire.emailTemplates.managerDescription')}
          </p>
        </div>
        {isAdmin && (
          <Button size="sm" onClick={() => openNew()} className="gap-1.5 rounded-xl">
            <Plus className="w-4 h-4" /> {t('hire.emailTemplates.newTemplate')}
          </Button>
        )}
      </div>

      {/* Template groups */}
      {groupedTemplates.map(group => {
        const Icon = group.icon;
        return (
          <div key={group.value} className="space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                <Icon className={`w-4 h-4 ${group.color}`} />
                {t(group.labelKey)}
              </h3>
              {isAdmin && group.templates.length === 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs gap-1 text-accent"
                  onClick={() => openNew(group.value)}
                >
                  <Plus className="w-3 h-3" /> {t('hire.emailTemplates.createDefault')}
                </Button>
              )}
            </div>

            {group.templates.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="py-5 text-center text-sm text-muted-foreground">
                  {t('hire.emailTemplates.emptyState')}
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2">
                {group.templates.map(tpl => (
                  <Card key={tpl.id} className="hover:shadow-sm transition-shadow">
                    <CardContent className="p-4 flex items-center justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm truncate">{tpl.name}</span>
                          {tpl.is_default && (
                            <Badge variant="secondary" className="text-xs gap-1">
                              <Star className="w-3 h-3" /> {t('hire.emailTemplates.defaultBadge')}
                            </Badge>
                          )}
                          {!tpl.is_active && (
                            <Badge variant="outline" className="text-xs text-muted-foreground">{t('hire.emailTemplates.inactiveBadge')}</Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground truncate mt-0.5">{tpl.subject}</p>
                      </div>
                      {isAdmin && (
                        <div className="flex gap-1 shrink-0">
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(tpl)}>
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-destructive"
                            onClick={() => setDeleteTemplateTarget({ id: tpl.id, name: tpl.name })}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {/* ────────────── Edit / Create Dialog ────────────── */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto rounded-xl">
          <DialogHeader>
            <DialogTitle className="font-display">
              {editing?.id ? t('hire.emailTemplates.editTemplate') : t('hire.emailTemplates.newEmailTemplate')}
            </DialogTitle>
            <p className="text-xs text-muted-foreground">
              {editing?.id
                ? t('hire.emailTemplates.editDescription')
                : t('hire.emailTemplates.newDescription')}
            </p>
          </DialogHeader>

          {editing && (
            <div className="space-y-5 py-2">
              {/* Name & Type row */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">{t('hire.emailTemplates.templateName')}</label>
                  <Input
                    value={editing.name || ''}
                    onChange={e => setEditing(prev => ({ ...prev, name: e.target.value }))}
                    placeholder={t('hire.emailTemplates.templateNamePlaceholder')}
                    className="rounded-xl"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">{t('hire.emailTemplates.stage')}</label>
                  <Select
                    value={editing.template_type || 'general'}
                    onValueChange={v => {
                      const defaults = defaultTemplates[v];
                      setEditing(prev => ({
                        ...prev,
                        template_type: v,
                        // Pre-fill with defaults if body is empty
                        ...((!prev?.body_html?.trim() && defaults) ? {
                          subject: defaults.subject,
                          body_html: defaults.body_html,
                        } : {}),
                      }));
                    }}
                  >
                    <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TEMPLATE_TYPES.map(type => {
                        const TIcon = type.icon;
                        return (
                          <SelectItem key={type.value} value={type.value}>
                            <span className="flex items-center gap-2">
                              <TIcon className={`w-3.5 h-3.5 ${type.color}`} />
                              {t(type.labelKey)}
                            </span>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Subject */}
              <div className="space-y-1.5">
                <label className="text-sm font-medium">{t('hire.emailTemplates.emailSubject')}</label>
                <Input
                  value={editing.subject || ''}
                  onChange={e => setEditing(prev => ({ ...prev, subject: e.target.value }))}
                  placeholder={restoreTemplateVariables(t('hire.emailTemplates.subjectPlaceholder'))}
                  className="rounded-xl"
                />
                <p className="text-[11px] text-muted-foreground">
                  {t('hire.emailTemplates.subjectHelp')}
                </p>
              </div>

              {/* Dynamic fields / variables */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium">{t('hire.emailTemplates.insertDynamicFields')}</label>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-[260px] text-xs">
                      {t('hire.emailTemplates.dynamicFieldsHelp')}
                    </TooltipContent>
                  </Tooltip>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {filteredVars.map(v => (
                    <Tooltip key={v.token}>
                      <TooltipTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-xs h-7 gap-1 rounded-lg"
                          onClick={() => insertVariable(v.token)}
                        >
                          + {v.label}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="text-xs">
                        {v.description}
                      </TooltipContent>
                    </Tooltip>
                  ))}
                </div>
              </div>

              {/* Body editor with Visual / Code tabs */}
              <div className="space-y-1.5">
                <Tabs value={editorTab} onValueChange={setEditorTab}>
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium">{t('hire.emailTemplates.emailBody')}</label>
                    <TabsList className="h-8">
                      <TabsTrigger value="visual" className="text-xs gap-1 px-3 h-7">
                        <Eye className="w-3 h-3" /> {t('hire.emailTemplates.preview')}
                      </TabsTrigger>
                      <TabsTrigger value="code" className="text-xs gap-1 px-3 h-7">
                        <Code className="w-3 h-3" /> {t('hire.emailTemplates.edit')}
                      </TabsTrigger>
                    </TabsList>
                  </div>

                  <TabsContent value="visual" className="mt-2">
                    <div
                      className="border rounded-xl p-5 bg-white min-h-[220px] prose prose-sm max-w-none text-foreground"
                      dangerouslySetInnerHTML={{ __html: editing.body_html || `<p class="text-muted-foreground">${t('hire.emailTemplates.noContentYet')}</p>` }}
                    />
                  </TabsContent>

                  <TabsContent value="code" className="mt-2">
                    <textarea
                      value={editing.body_html || ''}
                      onChange={e => setEditing(prev => ({ ...prev, body_html: e.target.value }))}
                      placeholder={t('hire.emailTemplates.bodyPlaceholder')}
                      rows={12}
                      className="w-full rounded-xl border border-input bg-background px-3 py-2 text-sm font-mono focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-y"
                    />
                    <p className="text-[11px] text-muted-foreground mt-1">
                      {t('hire.emailTemplates.htmlHelp', { token: '{{candidate_name}}' })}
                    </p>
                  </TabsContent>
                </Tabs>
              </div>

              {/* Toggles */}
              <div className="flex items-center gap-6 pt-1">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <Switch
                    checked={editing.is_default ?? false}
                    onCheckedChange={v => setEditing(prev => ({ ...prev, is_default: v }))}
                  />
                  {t('hire.emailTemplates.useAsDefault')}
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <Switch
                    checked={editing.is_active ?? true}
                    onCheckedChange={v => setEditing(prev => ({ ...prev, is_active: v }))}
                  />
                  {t('hire.emailTemplates.active')}
                </label>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-xl" onClick={() => setEditDialog(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              className="gradient-accent text-accent-foreground hover:opacity-90 rounded-xl"
              onClick={handleSave}
              disabled={upsertTemplate.isPending}
            >
              {upsertTemplate.isPending ? t('common.saving') : t('hire.emailTemplates.saveTemplate')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTemplateTarget} onOpenChange={(open) => { if (!open) setDeleteTemplateTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('hire.emailTemplates.deleteTemplate')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('hire.emailTemplates.deleteDescription', { name: deleteTemplateTarget?.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={() => { if (deleteTemplateTarget) { deleteTemplate.mutate(deleteTemplateTarget.id); setDeleteTemplateTarget(null); } }}>
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
